import * as fs from 'fs';
import * as path from 'path';
import { colors } from './const';
import { resolveSdkPackageName } from './detect-framework';
import { runCommandSync } from './utils/exec';

const c = colors;

export const RANK_ORDER_PACKAGES = [
  '@dnd-kit/core',
  '@dnd-kit/sortable',
  '@dnd-kit/utilities',
] as const;

export const MCQ_OTHER_PICKER_PACKAGES = [
  'react-international-phone',
  'react-datepicker',
  'date-fns',
] as const;

const CORE_PEER_PACKAGES = ['@tanstack/react-query', 'react-icons'] as const;

/** Tier 1 + Tier 2 — bundle ships all 11 question types including RANK_ORDER. */
export const BUNDLE_ALWAYS_PACKAGES = [
  ...CORE_PEER_PACKAGES,
  ...RANK_ORDER_PACKAGES,
] as const;

export const BUNDLE_ALWAYS_DEV_PACKAGES = ['@types/node'] as const;

export interface BundleDepsManifest {
  dependencies: Record<string, string>;
  devDependencies: Record<string, string>;
}

function getSdkTemplatesRoot(): string {
  return path.join(__dirname, '..', '..', 'templates');
}

export function loadBundleDepsManifest(): BundleDepsManifest {
  const manifestPath = path.join(getSdkTemplatesRoot(), 'vite-scaffold', 'package.deps.json');
  if (fs.existsSync(manifestPath)) {
    try {
      const raw = JSON.parse(fs.readFileSync(manifestPath, 'utf8')) as BundleDepsManifest;
      return {
        dependencies: raw.dependencies ?? {},
        devDependencies: raw.devDependencies ?? {},
      };
    } catch {
      // fall through to defaults
    }
  }
  return {
    dependencies: {
      '@dnd-kit/core': '^6.3.1',
      '@dnd-kit/sortable': '^10.0.0',
      '@dnd-kit/utilities': '^3.2.2',
      '@tanstack/react-query': '^5.0.0',
      'react-icons': '^5.5.0',
    },
    devDependencies: {
      '@types/node': '^22.15.29',
    },
  };
}

export interface MergeDepsResult {
  merged: boolean;
  addedDependencies: string[];
  addedDevDependencies: string[];
}

/** Add missing dependency keys only — never overwrite existing versions. */
export function mergeDepsIntoPackageJson(projectRoot: string): MergeDepsResult {
  const pkgPath = path.join(projectRoot, 'package.json');
  if (!fs.existsSync(pkgPath)) {
    return { merged: false, addedDependencies: [], addedDevDependencies: [] };
  }

  const manifest = loadBundleDepsManifest();
  const pkg = JSON.parse(fs.readFileSync(pkgPath, 'utf8')) as Record<string, unknown>;
  const deps = (pkg.dependencies as Record<string, string> | undefined) ?? {};
  const devDeps = (pkg.devDependencies as Record<string, string> | undefined) ?? {};

  const addedDependencies: string[] = [];
  const addedDevDependencies: string[] = [];

  for (const [name, version] of Object.entries(manifest.dependencies)) {
    if (!deps[name]) {
      deps[name] = version;
      addedDependencies.push(name);
    }
  }
  for (const [name, version] of Object.entries(manifest.devDependencies)) {
    if (!devDeps[name]) {
      devDeps[name] = version;
      addedDevDependencies.push(name);
    }
  }

  if (!addedDependencies.length && !addedDevDependencies.length) {
    return { merged: false, addedDependencies, addedDevDependencies };
  }

  pkg.dependencies = deps;
  pkg.devDependencies = devDeps;
  fs.writeFileSync(pkgPath, `${JSON.stringify(pkg, null, 2)}\n`, 'utf8');
  return { merged: true, addedDependencies, addedDevDependencies };
}

export function getBundleAlwaysPackageNames(): string[] {
  const manifest = loadBundleDepsManifest();
  return [
    ...Object.keys(manifest.dependencies),
    ...Object.keys(manifest.devDependencies),
  ];
}

export interface InstallBundleDepsResult {
  ok: boolean;
  installed: string[];
  skipped: boolean;
}

/** Merge package.json keys then npm install only missing packages (skip network when all present). */
export function installBundleDepsIfMissing(projectRoot: string): InstallBundleDepsResult {
  const mergeResult = mergeDepsIntoPackageJson(projectRoot);

  const packages = getBundleAlwaysPackageNames();
  const missingFromPkg = getMissingPackages(projectRoot, packages);
  const missingFromNodeModules = getUninstalledNodeModules(projectRoot, packages);
  const missing = [...new Set([...missingFromPkg, ...missingFromNodeModules])];

  if (!missing.length) {
    console.log(`${c.green}OK:${c.reset} Bundle deps — all packages already installed`);
    return { ok: true, installed: [], skipped: true };
  }

  const cmd = formatNpmInstallCommand(missing);
  if (!cmd) {
    return { ok: true, installed: [], skipped: true };
  }

  if (mergeResult.merged) {
    console.log(
      `${c.dim}Merged into package.json: ${[...mergeResult.addedDependencies, ...mergeResult.addedDevDependencies].join(', ')}${c.reset}`,
    );
  }

  console.log(`${c.dim}Installing missing bundle deps: ${missing.join(', ')}${c.reset}`);
  const ok = runCommandSync('npm', ['install', ...missing], { cwd: projectRoot });
  if (!ok) {
    console.log(`${c.red}FAIL:${c.reset} npm install failed — run manually: ${cmd}`);
    return { ok: false, installed: missing, skipped: false };
  }

  console.log(`${c.green}OK:${c.reset} Installed bundle deps (${missing.join(', ')})`);
  return { ok: true, installed: missing, skipped: false };
}

function readPackageJson(projectRoot: string): Record<string, unknown> | null {
  const pkgPath = path.join(projectRoot, 'package.json');
  if (!fs.existsSync(pkgPath)) return null;
  try {
    return JSON.parse(fs.readFileSync(pkgPath, 'utf8')) as Record<string, unknown>;
  } catch {
    return null;
  }
}

export function readInstalledPackages(projectRoot: string): Set<string> {
  const pkg = readPackageJson(projectRoot);
  if (!pkg) return new Set();

  const installed = new Set<string>();
  for (const section of ['dependencies', 'devDependencies'] as const) {
    const deps = pkg[section] as Record<string, string> | undefined;
    if (!deps) continue;
    for (const name of Object.keys(deps)) installed.add(name);
  }
  return installed;
}

export function getCorePackages(projectRoot: string): string[] {
  const pkg = readPackageJson(projectRoot);
  const sdk =
    resolveSdkPackageName({
      dependencies: pkg?.dependencies as Record<string, string> | undefined,
      devDependencies: pkg?.devDependencies as Record<string, string> | undefined,
    }) ?? '@explorer02/cfm-survey-sdk';

  return [sdk, ...CORE_PEER_PACKAGES];
}

export function getSurveyTypeDeps(
  surveyTypes: string[],
  hasMcqOtherPickers = false,
): string[] {
  const packages: string[] = [];
  if (surveyTypes.includes('RANK_ORDER')) {
    packages.push(...RANK_ORDER_PACKAGES);
  }
  if (hasMcqOtherPickers) {
    packages.push(...MCQ_OTHER_PICKER_PACKAGES);
  }
  return [...new Set(packages)];
}

export function getMissingPackages(projectRoot: string, packages: string[]): string[] {
  const installed = readInstalledPackages(projectRoot);
  return packages.filter((p) => !installed.has(p));
}

function isPackageInNodeModules(projectRoot: string, packageName: string): boolean {
  const parts = packageName.split('/');
  const modPath = path.join(projectRoot, 'node_modules', ...parts);
  return fs.existsSync(modPath);
}

/** Packages listed in package.json but absent from node_modules. */
export function getUninstalledNodeModules(
  projectRoot: string,
  packages: string[],
): string[] {
  const listed = readInstalledPackages(projectRoot);
  return packages.filter((p) => listed.has(p) && !isPackageInNodeModules(projectRoot, p));
}

export function formatNpmInstallCommand(
  packages: string[],
  dev = false,
): string | null {
  if (!packages.length) return null;
  if (dev) return `npm install -D ${packages.join(' ')}`;
  return `npm install ${packages.join(' ')}`;
}

export function printInstallBlock(options: {
  title: string;
  packages: string[];
  dev?: boolean;
  source?: string;
  projectRoot: string;
}): boolean {
  const missing = getMissingPackages(options.projectRoot, options.packages);
  const border = '═'.repeat(56);

  if (!missing.length) {
    console.log(`${c.green}OK:${c.reset} ${options.title} — all packages already installed`);
    return true;
  }

  const cmd = formatNpmInstallCommand(missing, options.dev);
  console.log('');
  console.log(border);
  console.log(`  ${options.title}`);
  console.log(border);
  console.log('');
  if (cmd) console.log(cmd);
  if (options.source) console.log(`\nSource: ${options.source}`);
  console.log(border);
  console.log('');
  return false;
}

export function printSurveyTypeInstallBlock(
  projectRoot: string,
  surveyTypes: string[],
  hasMcqOtherPickers = false,
): boolean {
  const packages = getSurveyTypeDeps(surveyTypes, hasMcqOtherPickers);
  if (!packages.length) {
    console.log(
      `${c.dim}Survey-type deps: none required for inventory [${surveyTypes.join(', ')}]${c.reset}`,
    );
    return true;
  }

  return printInstallBlock({
    title: 'INSTALL SURVEY-TYPE DEPS (run once before Phase 6)',
    packages,
    projectRoot,
    source: 'setup/conditional-deps.md',
  });
}

export function checkTailwindGlobalsImport(projectRoot: string): boolean {
  const globalsCandidates = [
    'src/app/globals.css',
    'src/styles/globals.css',
    'src/index.css',
    'app/globals.css',
  ];
  for (const rel of globalsCandidates) {
    const abs = path.join(projectRoot, rel);
    if (!fs.existsSync(abs)) continue;
    const content = fs.readFileSync(abs, 'utf8');
    if (/@import\s+["']tailwindcss["']/.test(content)) return true;
  }
  return false;
}
